<template>
  <div class="dvs-field">
    <label v-if="label" class="dvs-form-label" :for="id">
      {{ label }}
    </label>

    <input
      v-if="type !== 'textarea'"
      :id="id"
      class="dvs-input"
      :class="{ 'is-error': error }"
      :type="type"
      :inputmode="inputmode"
      :min="min"
      :max="max"
      :step="step"
      :placeholder="placeholder"
      :value="modelValue"
      :maxlength="maxlength"
      @input="handleInput"
      @blur="$emit('blur')"
      @focus="$emit('focus')"
    />

    <textarea
      v-else
      :id="id"
      class="dvs-textarea"
      :class="{ 'is-error': error }"
      :placeholder="placeholder"
      :value="modelValue"
      :maxlength="maxlength"
      @input="handleInput"
      @blur="$emit('blur')"
      @focus="$emit('focus')"
    ></textarea>

    <p v-if="error && errorMessage" class="dvs-error-message">
      {{ errorMessage }}
    </p>

    <p v-else-if="helper" class="dvs-helper-text">
      {{ helper }}
    </p>

    <p v-if="showCounter && maxlength" class="dvs-counter">
      {{ currentLength }}/{{ maxlength }} caracteres
    </p>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: "",
  },
  id: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  inputmode: {
    type: String,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: "",
  },
  min: {
    type: [String, Number],
    default: undefined,
  },
  max: {
    type: [String, Number],
    default: undefined,
  },
  step: {
    type: [String, Number],
    default: undefined,
  },
  maxlength: {
    type: [String, Number],
    default: undefined,
  },
  maxDigits: {
    type: [String, Number],
    default: undefined,
  },
  helper: {
    type: String,
    default: "",
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  showCounter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "blur", "focus"]);

const currentLength = computed(() => {
  return String(props.modelValue || "").length;
});

function handleInput(event) {
  const value = props.maxDigits
    ? limitDigits(event.target.value, Number(props.maxDigits))
    : event.target.value;

  if (value !== event.target.value) {
    event.target.value = value;
  }

  emit("update:modelValue", value);
}

function limitDigits(value, maxDigits) {
  if (!Number.isFinite(maxDigits) || maxDigits <= 0) return value;

  const raw = String(value ?? "").replace(/[^\d.]/g, "");
  const firstDot = raw.indexOf(".");

  if (firstDot === -1) {
    return raw.slice(0, maxDigits);
  }

  const whole = raw.slice(0, firstDot).slice(0, maxDigits);
  const decimal = raw
    .slice(firstDot + 1)
    .replace(/\./g, "")
    .slice(0, 2); // ← 3 → 2

  return decimal ? `${whole || "0"}.${decimal}` : `${whole}.`;
}
</script>
